import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import MessageEditor from "components/forms/MessageEditor";
import { useToast } from "components/toast";
import { themevals } from "theme/themevals";
import { getLead } from "api/Leads";
import { sendProposalToContact } from "api/Proposals";

/**
 * "Send to another contact" on a SENT proposal whose lead has colleagues
 * at the same organization (2026-09-28). Creates the next version of this
 * proposal on the chosen contact's lead - same document, same prices - and
 * sends it with its own link. Renders nothing when there is nobody else.
 */
export default function SendToContactButton({ proposal, onSent }: { proposal: any; onSent: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const leadId: string | null = proposal?.client?.stage === "lead" ? proposal.client.id : null;
  const [others, setOthers] = useState<Array<{ id: string; name: string; contactName: string | null }>>([]);
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<string>("");
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!leadId) return;
    getLead(leadId).then((l: any) => { if (!l?.error) setOthers(Array.isArray(l.sameOrg) ? l.sameOrg : []); });
  }, [leadId]);

  useEffect(() => {
    if (!pick) return setTo("");
    getLead(pick).then((l: any) => setTo(l?.contactEmail ?? ""));
  }, [pick]);

  if (!leadId || proposal.status === "draft" || others.length === 0) return null;

  const send = async (skipEmail = false) => {
    if (!pick) return toast.add("Pick the contact.", "var(--theme-ui-colors-danger)");
    if (!skipEmail && !to.trim()) return toast.add("An email address is needed.", "var(--theme-ui-colors-danger)");
    setBusy(true);
    const res: any = await sendProposalToContact(proposal.id, { leadId: pick, sentTo: to.trim(), message: message.trim() || undefined, requirePassword: password, skipEmail });
    setBusy(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    if (skipEmail && res?.shareUrl) {
      try { await navigator.clipboard.writeText(res.shareUrl); } catch { /* link is on the new version */ }
    }
    toast.add(`Sent as v${res?.version ?? ""} to ${others.find((o) => o.id === pick)?.contactName ?? "the contact"}.`, "var(--theme-ui-colors-success)");
    setOpen(false);
    onSent();
    if (res?.id) navigate(`/proposals/${res.id}`);
  };

  const input: React.CSSProperties = { padding: "7px 9px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: 6, fontSize: 13, fontFamily: "Poppins", width: "100%", boxSizing: "border-box" };

  return (
    <>
      <BasicButton outline scheme="secondary" keepText onClick={() => { setOpen(true); setPick(others.length === 1 ? others[0].id : ""); }}>
        Send to another contact
      </BasicButton>
      {open && (
        <BasicModal
          title="Send this proposal to another contact"
          size="md"
          cancel_click={() => setOpen(false)}
          cancel_text="Cancel"
          confirm_text={busy ? "Sending\u2026" : "Send"}
          confirm_click={() => send(false)}
          submitting={busy}
        >
          <div style={{ fontSize: 12, fontFamily: "Poppins", textAlign: "left", color: themevals.colors.dkgrey, marginBottom: 12 }}>
            Creates the next version of this proposal on that contact&apos;s lead. Same document, same prices; its own link, so their opens are tracked on their lead.
          </div>
          <select style={{ ...input, marginBottom: 10 }} value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">Choose a contact at this organization</option>
            {others.map((o) => (
              <option key={o.id} value={o.id}>{o.contactName || o.name}</option>
            ))}
          </select>
          <input style={{ ...input, marginBottom: 10 }} type="email" placeholder="Email" value={to} onChange={(e) => setTo(e.target.value)} />
          <div style={{ marginBottom: 10 }}>
            <MessageEditor
              value={message}
              onChange={setMessage}
              placeholder="Message (optional). A View proposal button sits above it; the link repeats below."
              email={{
                shareUrl: null,
                password: null,
                hasPassword: password,
                includePassword: true,
                fromName: proposal.payload?.contact?.name || "The sender on the proposal",
                replyTo: proposal.payload?.contact?.email ?? null,
                subject: `${proposal.prospectName} \u2014 preliminary pricing`,
                to: to || null,
              }}
            />
          </div>
          <div
            onClick={() => setPassword((v) => !v)}
            style={{ display: "flex", gap: 8, alignItems: "center", fontFamily: "Poppins", fontSize: 13, textAlign: "left", cursor: "pointer", color: "#1a2332" }}
          >
            <input type="checkbox" checked={password} readOnly style={{ width: 16, height: 16, margin: 0, flexShrink: 0 }} />
            <span>Protect the link with a password</span>
          </div>
          <div style={{ textAlign: "left", marginTop: 10 }}>
            <button type="button" disabled={busy} onClick={() => send(true)} style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0, fontFamily: "Poppins", fontSize: 12, fontWeight: 500 }}>
              Copy link instead of emailing
            </button>
          </div>
        </BasicModal>
      )}
    </>
  );
}
